import React from 'react';
import { Card, CardContent } from './ui/card';
import { Shield, BadgeDollarSign, BarChart3, Clock, Zap, Leaf, Home, TrendingUp } from 'lucide-react';

const reasons = [
  {
    icon: BadgeDollarSign,
    title: 'Save Thousands',
    description: 'Fully electrified homes can save $2,000-$4,000+ per year on energy bills by eliminating gas and maximising solar.',
    highlight: '$100k+ lifetime savings'
  },
  {
    icon: Zap,
    title: '100% Independent',
    description: 'We provide unbiased comparisons with no sales pressure. Our only goal is helping you find the best solution for your home.',
    highlight: 'No hidden agendas'
  },
  {
    icon: BarChart3,
    title: 'Easy Comparison',
    description: 'Complex energy decisions made simple. Compare solar, batteries, heat pumps, and more in one place with clear pricing.',
    highlight: 'All products, one place'
  },
  {
    icon: Clock,
    title: 'Quick & Free',
    description: 'Get personalised recommendations in under 2 minutes. No cost, no obligation, no spam — just helpful comparisons.',
    highlight: '2-minute process'
  }
];

const electrificationStats = [
  { value: '3M+', label: 'Aussie homes with solar', icon: Home },
  { value: '70%', label: 'Potential bill savings', icon: TrendingUp },
  { value: '25 yrs', label: 'Average system lifespan', icon: Clock },
  { value: '0', label: 'Gas bills after switch', icon: Leaf }
];

export const WhyCompare = () => {
  return (
    <section id="about" className="py-16 md:py-20 bg-gradient-to-b from-white to-orange-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-orange-100 px-4 py-2 rounded-full mb-4">
            <Zap className="w-4 h-4 text-cyo-orange" />
            <span className="text-sm font-semibold text-cyo-orange">Why Choose Us</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Why Compare with <span className="text-cyo-orange">CompareYourOptions.com.au</span>?
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            We help Australians make smarter energy decisions and transition to fully electrified, 
            energy-efficient homes.
          </p>
        </div>

        {/* Reasons Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {reasons.map((reason) => {
            const Icon = reason.icon;
            return (
              <Card
                key={reason.title}
                className="bg-white border-0 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <CardContent className="p-6">
                  {/* Icon */}
                  <div className="w-14 h-14 bg-gradient-to-br from-cyo-orange to-orange-400 rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-orange-200">
                    <Icon className="w-7 h-7 text-white" />
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {reason.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-500 text-sm mb-3">
                    {reason.description}
                  </p>

                  {/* Highlight */}
                  <span className="inline-block text-xs font-semibold text-cyo-orange bg-orange-50 px-3 py-1 rounded-full">
                    {reason.highlight}
                  </span>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Electrification Stats Banner */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl p-8 md:p-10">
          <h3 className="text-xl md:text-2xl font-bold text-white text-center mb-8">
            The Home Electrification Movement is <span className="text-cyo-orange">Growing Fast</span>
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {electrificationStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-6 h-6 text-cyo-orange" />
                  </div>
                  <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
